import { ClassType, EntityType, formatDays } from '@frontend/common';
import { Spinner, StatusBadge } from '@frontend/components/common';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { CommonMessage } from '@frontend/lang';
import { ScheduleClient } from '@frontend/report/api';
import { Schedule } from '@frontend/report/types';
import { FormattedMessage } from 'react-intl';

import OverviewHeader from '../../applications/header/overview-header';
import { TabFilter } from '../../applications/tab-filter/tab-filter.component';
import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { usePlatformModalHost } from '../../modal/platform-modal-host/platform-modal-host.context';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import { useSchedules } from './schedule.controller';

export const Schedules = () => {
    const viewProps = useSchedules();
    if (!viewProps.currentUser) {
        return (
            <div
                key='pudo-transactions'
                className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
                <OverviewHeader
                    title={
                        <div className='flex gap-2'>
                            <span>{CommonMessage.LICENSES.ANALYTICS.DEFAULT}</span>
                            <span>/</span>
                            <span>{CommonMessage.OBJECTS.SCHEDULE.PLURAL}</span>
                        </div>
                    }
                />
                <Spinner />
            </div>
        );
    }
    return (
        <div
            key='pudo-transactions'
            className='size-lf-stretch bg-background-default inline-flex w-full flex-col items-start justify-start gap-6'>
            <OverviewHeader
                title={
                    <div className='flex gap-2'>
                        <span>{CommonMessage.LICENSES.ANALYTICS.DEFAULT}</span>
                        <span>/</span>
                        <span>{CommonMessage.OBJECTS.SCHEDULE.PLURAL}</span>
                    </div>
                }
            />
            <div className='inline-flex flex-col items-start justify-start gap-4 self-stretch'>
                <TabFilter
                    keys={['my_schedules', 'all_schedules']}
                    displayMap={
                        new Map([
                            [
                                'my_schedules',
                                <FormattedMessage
                                    id='feature.schedules.tabs.my-schedules'
                                    defaultMessage='My schedules'
                                    key='feature.schedules.tabs.my-schedules'
                                />
                            ],
                            [
                                'all_schedules',
                                <FormattedMessage
                                    id='feature.schedules.tabs.all-schedules'
                                    defaultMessage='All schedules'
                                    key='feature.schedules.tabs.all-schedules'
                                />
                            ]
                        ])
                    }
                    onChange={viewProps.setActiveTab}
                    useQueryParams={{ key: 'tab' }}
                />
                <ScheduleList
                    key={viewProps.activeTab}
                    {...viewProps}
                />
            </div>
        </div>
    );
};

const ScheduleList = (viewProps: ReturnType<typeof useSchedules>) => {
    const { openPlatformDetail } = usePlatformModalHost();
    switch (viewProps.activeTab) {
        case 'my_schedules':
            return (
                <ReactiveObjectList<Schedule>
                    key='my_schedules'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({
                            entityType: EntityType.SCHEDULE,
                            id: obj.id
                        })
                    }
                    id={'my_schedules-schedules-list'}
                    fetch={(arg) =>
                        ScheduleClient.fetchSchedules({
                            ...arg,
                            account_id: viewProps.currentAccount,
                            receivers: viewProps.currentUser ? [viewProps.currentUser.id] : undefined
                        })
                    }
                    dispatch={viewProps.dispatch}
                    columns={{
                        columns: ['name', 'receivers', 'status', 'every_days', 'last_executed_timestamp'],
                        headerOverwrite: new Map([
                            ['every_days', CommonMessage.OBJECTS.SCHEDULE.RUNS_EVERY],
                            ['status', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS]
                        ]),
                        customCells: new Map([
                            [
                                'receivers',
                                (obj) => {
                                    if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                        return (
                                            <td className={defaultCellClassName}>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.receivers[0]}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.receivers[0]}
                                                    entityType={EntityType.USER}
                                                    key={obj.row.original.receivers[0]}
                                                />
                                                {obj.row.original.receivers.length > 1 && (
                                                    <MultiEntityButton<string>
                                                        entities={obj.row.original.receivers.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj}
                                                                entityType={EntityType.USER}
                                                                key={obj}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            ['every_days', (obj) => <td className={defaultCellClassName}>{formatDays(obj.row.original.every_days)}</td>],
                            [
                                'status',
                                (obj) => {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <StatusBadge
                                                value={
                                                    obj.row.original.enabled
                                                        ? CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED
                                                        : CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED
                                                }
                                                colorMap={
                                                    new Map([
                                                        [[CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED], ClassType.SUCCESS],
                                                        [[CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED], ClassType.WARNING]
                                                    ])
                                                }
                                            />
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.SCHEDULE}
                    tableProps={{
                        defaultHeight: true,
                        hideFooter: true
                    }}
                />
            );
        case 'all_schedules':
            return (
                <ReactiveObjectList<Schedule>
                    key='all_schedules'
                    useStaticListId
                    onSelectElement={(obj) =>
                        openPlatformDetail({
                            entityType: EntityType.SCHEDULE,
                            id: obj.id
                        })
                    }
                    id={'all_schedules-schedules-list'}
                    fetch={(arg) => ScheduleClient.fetchSchedules({ ...arg, account_id: viewProps.currentAccount })}
                    dispatch={viewProps.dispatch}
                    columns={{
                        columns: ['name', 'receivers', 'status', 'every_days', 'last_executed_timestamp'],
                        headerOverwrite: new Map([
                            ['every_days', CommonMessage.OBJECTS.SCHEDULE.RUNS_EVERY],
                            ['status', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS]
                        ]),
                        customCells: new Map([
                            [
                                'receivers',
                                (obj) => {
                                    if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                                        return (
                                            <td className='text-brand-primary align-center truncate p-2 text-xs'>
                                                <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            key={obj.row.original.receivers[0]}
                                            className={defaultCellClassName}>
                                            <div className='flex gap-2.5 truncate text-sm font-medium'>
                                                <PlatformObjectBadge
                                                    id={obj.row.original.receivers[0]}
                                                    entityType={EntityType.USER}
                                                    key={obj.row.original.receivers[0]}
                                                />
                                                {obj.row.original.receivers.length > 1 && (
                                                    <MultiEntityButton<string>
                                                        entities={obj.row.original.receivers.slice(1)}
                                                        render={(obj) => (
                                                            <PlatformObjectBadge
                                                                id={obj}
                                                                entityType={EntityType.USER}
                                                                key={obj}
                                                            />
                                                        )}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    );
                                }
                            ],
                            ['every_days', (obj) => <td className={defaultCellClassName}>{formatDays(obj.row.original.every_days)}</td>],
                            [
                                'status',
                                (obj) => {
                                    return (
                                        <td className={defaultCellClassName}>
                                            <StatusBadge
                                                value={
                                                    obj.row.original.enabled
                                                        ? CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED
                                                        : CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED
                                                }
                                                colorMap={
                                                    new Map([
                                                        [[CommonMessage.OBJECTS.COMMON_FIELDS.ENABLED], ClassType.SUCCESS],
                                                        [[CommonMessage.OBJECTS.COMMON_FIELDS.DISABLED], ClassType.WARNING]
                                                    ])
                                                }
                                            />
                                        </td>
                                    );
                                }
                            ]
                        ])
                    }}
                    type={EntityType.SCHEDULE}
                    tableProps={{
                        defaultHeight: true,
                        hideFooter: true
                    }}
                />
            );
    }
};
